import { useEffect, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { selectIsGBUser } from 'reducers/selectors';
import { selectFlagHelpArticles, selectHelpArticlesByCategory, selectHelpArticlesScreenConfigs, selectHelpMoreArticles, } from '../selectors';
import { getHelpCategoryArticles } from '../actions';
import { HELP_ARTICLES_KEY } from '../constants';
export const useHelpArticlesConfig = (articlesKey) => {
    const dispatch = useAppDispatch();
    const screenConfig = useAppSelector((store) => selectHelpArticlesScreenConfigs(store, articlesKey ?? HELP_ARTICLES_KEY.DEFAULT));
    const featureFlagArticles = useAppSelector(selectFlagHelpArticles);
    const isGBUser = useAppSelector(selectIsGBUser);
    const locale = isGBUser ? 'en' : 'en-us';
    const { headerTitle, omitFaq, categoryId, categoryTitle, categoryEnabled, articles: articleIds, moreArticlesKey, } = screenConfig ?? {};
    const relatedTopicsTitle = headerTitle ?? 'Related topics';
    const moreArticlesConfig = useAppSelector((store) => selectHelpMoreArticles(store, moreArticlesKey));
    const moreArticlesTitle = moreArticlesConfig?.title;
    const moreArticleIds = moreArticlesConfig?.articles;
    const relatedTopicArticles = useMemo(() => {
        const deDupedArticleIds = [...new Set(articleIds)];
        return deDupedArticleIds
            .map((articleId) => {
            if (featureFlagArticles?.[articleId]) {
                return { ...featureFlagArticles[articleId], article_id: articleId };
            }
            return null;
        })
            .filter((article) => article !== null);
    }, [articleIds, featureFlagArticles]);
    const relatedTopicMoreArticles = useMemo(() => {
        const deDupedArticleIds = [...new Set(moreArticleIds)];
        return deDupedArticleIds
            .map((articleId) => {
            if (featureFlagArticles?.[articleId]) {
                return { ...featureFlagArticles[articleId], article_id: articleId };
            }
            return null;
        })
            .filter((article) => article !== null);
    }, [moreArticleIds, featureFlagArticles]);
    const relatedCategoryArticles = useAppSelector((store) => selectHelpArticlesByCategory(store, categoryId));
    const showSeeAll = useMemo(() => !!relatedTopicMoreArticles.length ||
        (!!relatedCategoryArticles.length && !!categoryEnabled), [categoryEnabled, relatedCategoryArticles, relatedTopicMoreArticles]);
    const initialArticles = useMemo(() => {
        if (relatedTopicMoreArticles.length) {
            return relatedTopicMoreArticles;
        }
        if (relatedCategoryArticles.length && showSeeAll) {
            return relatedCategoryArticles;
        }
        if (relatedTopicArticles.length) {
            return relatedTopicArticles;
        }
        return [];
    }, [
        relatedTopicMoreArticles,
        relatedCategoryArticles,
        relatedTopicArticles,
        showSeeAll,
    ]);
    useEffect(() => {
        if (!relatedTopicMoreArticles.length &&
            !relatedCategoryArticles.length &&
            categoryEnabled &&
            categoryId) {
            dispatch(getHelpCategoryArticles({
                locale,
                filter_category_id: categoryId,
            }));
        }
    }, [
        categoryEnabled,
        categoryId,
        dispatch,
        locale,
        relatedCategoryArticles.length,
        relatedTopicMoreArticles.length,
    ]);
    const relatedArticlesTitle = useMemo(() => {
        if (relatedCategoryArticles.length && categoryTitle && categoryEnabled) {
            return `Articles on ${categoryTitle}`;
        }
        if (relatedTopicMoreArticles.length && moreArticlesTitle) {
            return moreArticlesTitle;
        }
        return relatedTopicsTitle;
    }, [
        relatedCategoryArticles.length,
        categoryTitle,
        categoryEnabled,
        relatedTopicMoreArticles.length,
        moreArticlesTitle,
        relatedTopicsTitle,
    ]);
    return {
        omitFaq,
        relatedTopicArticles,
        relatedTopicsTitle,
        showSeeAll,
        initialArticles,
        relatedArticlesTitle,
    };
};
